import { Trans, useLingui } from '@lingui/react/macro';
import type { Diagnostic } from '@repo/contracts';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { blockingRulesForShift, type ProposalStack } from '@/domain/rotagen/proposal-day-data';

import { ShiftStackCard } from './shift-stack-card';

interface ProposalShiftStackProps {
  /** The deck of same-type, same-time shifts for a day. */
  stack: ProposalStack;
  /** All proposal diagnostics, used to explain the unfilled slots. */
  diagnostics: Diagnostic[] | undefined;
}

/**
 * A grouped "deck" of shifts in the stacked proposal view: a single coloured
 * card (`×N`, with an "N short" warning when any slot is unfilled) that opens a
 * popover breaking the deck down — the assigned workers and, for the unfilled
 * slots, the distinct scheduling rules that blocked candidates.
 */
export function ProposalShiftStack({ stack, diagnostics }: ProposalShiftStackProps) {
  const { t } = useLingui();
  const filled = stack.count - stack.shortCount;

  const assignees = stack.cells
    .filter((cell) => cell.filled && cell.worker)
    .map((cell) => cell.worker!.name);

  const blockingRules = [
    ...new Set(
      stack.cells
        .filter((cell) => !cell.filled)
        .flatMap((cell) => blockingRulesForShift(diagnostics, cell.shift.id)),
    ),
  ];

  return (
    <Popover>
      <PopoverTrigger className="w-full text-left">
        <ShiftStackCard
          typeName={stack.typeName}
          time={stack.time}
          count={stack.count}
          shortCount={stack.shortCount}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 space-y-2 text-[length:var(--body-font-size)] leading-[var(--body-line-height)]"
      >
        <div>
          <p className="font-semibold">{stack.typeName}</p>
          <p className="text-[var(--muted-foreground)]">{stack.time}</p>
        </div>
        <p className="text-[var(--muted-foreground)]">
          <Trans>
            {filled} of {stack.count} filled
          </Trans>
        </p>

        {assignees.length > 0 && (
          <div>
            <p className="font-medium">
              <Trans>Assigned</Trans>
            </p>
            <ul>
              {assignees.map((name, index) => (
                <li key={`${name}-${index}`}>{name}</li>
              ))}
            </ul>
          </div>
        )}

        {stack.shortCount > 0 && (
          <div className="border-t pt-2">
            <p className="font-medium text-destructive">
              {t`${stack.shortCount} unfilled — no one was assigned.`}
            </p>
            {blockingRules.length > 0 && (
              <div className="pt-1 text-[var(--muted-foreground)]">
                <span className="font-medium">
                  <Trans>Blocked by:</Trans>
                </span>{' '}
                {blockingRules.join(', ')}
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
